import type { ContractSourceMeta } from '@/contracts/questionnaire'
import type { LogicJump, Question } from '@/types/form'
import { z } from 'zod'

const QuestionContractSchema = z.custom<Question>(
  (value) => typeof value === 'object' && value !== null,
)

const LogicJumpContractSchema = z.custom<LogicJump>(
  (value) => typeof value === 'object' && value !== null,
)

export const QuestionGroupSourceMetaSchema = z.object({
  source: z.enum(['synthetic', 'database']),
  permissionScope: z.string().min(1),
  dataDate: z.string(),
  confidence: z.number().min(0).max(1),
})

export const QuestionGroupModeSchema = z.enum(['synced', 'copy'])
export type QuestionGroupMode = z.infer<typeof QuestionGroupModeSchema>

export const QuestionOutlineItemSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('question'),
    questionId: z.string(),
    groupInstanceId: z.null().optional(),
  }),
  z.object({
    kind: z.literal('group'),
    groupInstanceId: z.string(),
  }),
])
export type QuestionOutlineItem = z.infer<typeof QuestionOutlineItemSchema>

export const QuestionGroupTemplateContractSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  title: z.string(),
  description: z.string().optional(),
  category: z.string().optional(),
  tags: z.array(z.string()),
  questionIds: z.array(z.string()),
  questions: z.array(QuestionContractSchema),
  logicJumps: z.array(LogicJumpContractSchema),
  usageCount: z.number().int().nonnegative(),
  updatedAt: z.string(),
  sourceMeta: QuestionGroupSourceMetaSchema,
})
export type QuestionGroupTemplateContract = z.infer<typeof QuestionGroupTemplateContractSchema> & {
  questions: Question[]
  logicJumps: LogicJump[]
  sourceMeta: ContractSourceMeta
}

export const FormQuestionGroupInstanceContractSchema = z.object({
  id: z.string(),
  formId: z.string(),
  sourceGroupId: z.string(),
  mode: QuestionGroupModeSchema,
  title: z.string(),
  description: z.string().optional(),
  orderIndex: z.number(),
  collapsed: z.boolean(),
  questionIds: z.array(z.string()),
  questions: z.array(QuestionContractSchema).optional(),
  logicJumps: z.array(LogicJumpContractSchema).optional(),
  lockedReason: z.string().optional(),
  sourceMeta: QuestionGroupSourceMetaSchema,
})
export type FormQuestionGroupInstanceContract = z.infer<
  typeof FormQuestionGroupInstanceContractSchema
> & {
  questions?: Question[]
  logicJumps?: LogicJump[]
  sourceMeta: ContractSourceMeta
}

export const QuestionGroupMutationStateSchema = z.union([
  z.object({
    allowed: z.literal(true),
  }),
  z.object({
    allowed: z.literal(false),
    code: z.literal('synced_group_readonly'),
    message: z.string(),
  }),
])
export type QuestionGroupMutationState = z.infer<typeof QuestionGroupMutationStateSchema>

export function getQuestionGroupMutationState(
  instance: Pick<FormQuestionGroupInstanceContract, 'mode' | 'title'>,
): QuestionGroupMutationState {
  if (instance.mode === 'copy') return { allowed: true }

  return {
    allowed: false,
    code: 'synced_group_readonly',
    message: `「${instance.title}」是同步題組，題目需回到題組管理編輯。`,
  }
}
